import { deliveryClient } from '../../../lib/connect/clients';
import { makeEntityHooks } from '../_shared/hooks/makeEntityHooks';
// ProgramRoleAssignment — contact + tenant enum role_type (GDS Roles tab)
export const programRoleAssignmentHooks = makeEntityHooks({
    key: 'program role assignment',
    list: async (req)=>{
        const resp = await deliveryClient.listProgramRoleAssignments({
            programId: req.programId,
            roleCategory: 'INTERNAL',
            activeOnly: true
        });
        return {
            items: resp.assignments
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertProgramRoleAssignment({
            id: req.id,
            programId: req.programId,
            contactId: req.contactId,
            userId: req.userId ?? '',
            roleCategory: 'INTERNAL',
            roleTypeId: req.roleTypeId,
            isPrimary: req.isPrimary
        });
        // Carry the roster->grant outcome alongside the row: the tab explains
        // whether access followed the assignment (§4.2 access states).
        if (!resp.assignment) {
            throw new Error('upsert returned no assignment');
        }
        return Object.assign(resp.assignment, {
            accessState: resp.accessState
        });
    },
    remove: async (id)=>{
        await deliveryClient.deleteProgramRoleAssignment({
            id
        });
    },
    entityId: (a)=>a.id
});
// ProgramRole — legacy freetext (deprecated)
export const programRoleHooks = makeEntityHooks({
    key: 'program role',
    list: async (req)=>{
        const resp = await deliveryClient.listProgramRoles(req);
        return {
            items: resp.roles
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertProgramRole(req);
        return resp.role;
    },
    remove: async (id)=>{
        await deliveryClient.deleteProgramRole({
            id
        });
    },
    entityId: (r)=>r.id
});
// ProgramCapability — { id, programId, category, percentage, description }
export const programCapabilityHooks = makeEntityHooks({
    key: 'program capability',
    list: async (req)=>{
        const resp = await deliveryClient.listProgramCapabilities(req);
        return {
            items: resp.capabilities
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertProgramCapability(req);
        return resp.capability;
    },
    remove: async (id)=>{
        await deliveryClient.deleteProgramCapability({
            id
        });
    },
    entityId: (c)=>c.id
});
// ProgramCompanyAssignment — { id, programId, companyProfileId, active, type, fteCount, vacancies, ... }
// The terminationDate proto field is a Timestamp — not exposed in the basic
// form; left untouched on Upsert by passing an empty proto value, which
// pangaea treats as "no termination".
export const programCompanyHooks = makeEntityHooks({
    key: 'program company',
    list: async (req)=>{
        const resp = await deliveryClient.listProgramCompanies(req);
        return {
            items: resp.assignments
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertProgramCompanyAssignment(req);
        return resp.assignment;
    },
    remove: async (id)=>{
        await deliveryClient.deleteProgramCompanyAssignment({
            id
        });
    },
    entityId: (a)=>a.id
});
// ProgramCompanyStaffingLine — one position under a company assignment.
// fte_count / vacancies on ProgramCompanyAssignmentProto are server-derived
// from these rows once any exist (decision 1(c),
// major-push/7-29-specra/09-subcontractor-fte-drilldown.md), so every write
// here also invalidates the parent's 'program company' list — that's the
// whole mechanism behind the drawer's parent row refreshing with no reload.
export const programCompanyStaffingLineHooks = makeEntityHooks({
    key: 'program company staffing line',
    list: async (req)=>{
        const resp = await deliveryClient.listProgramCompanyStaffingLines({
            programId: req.programId,
            companyAssignmentId: req.companyAssignmentId ?? ''
        });
        return {
            items: resp.lines
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertProgramCompanyStaffingLine(req);
        return resp.line;
    },
    remove: async (req)=>{
        await deliveryClient.deleteProgramCompanyStaffingLine(req);
    },
    entityId: (l)=>l.id,
    extraInvalidateKeys: [
        'program company'
    ]
});
// ProgramContact — { id, programId, contactId, role }
export const programContactHooks = makeEntityHooks({
    key: 'program contact',
    list: async (req)=>{
        const resp = await deliveryClient.listProgramContacts(req);
        return {
            items: resp.contacts
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertProgramContact(req);
        return resp.contact;
    },
    remove: async (id)=>{
        await deliveryClient.deleteProgramContact({
            id
        });
    },
    entityId: (c)=>c.id
});
